import { openapiClient, unwrapResult } from '@/api/openapi-client';
import { useRestaurantStore } from '@/stores/restaurant.store';
import type { CreateCustomerBody, Customer, CustomerList } from '@restaurant/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { customerKeys } from './queryKeys';

const CUSTOMERS_PATH = '/api/franchises/{franchiseId}/customers';

export function useCustomers(search: string) {
  const franchiseId = useRestaurantStore((s) => s.selectedFranchiseId) ?? '';

  return useQuery({
    queryKey: customerKeys.list(franchiseId, search),
    queryFn: async () =>
      unwrapResult<CustomerList>(
        await openapiClient.GET(CUSTOMERS_PATH, {
          params: {
            path: { franchiseId },
            query: search ? { search, limit: 100 } : { limit: 100 },
          },
        }),
      ),
    enabled: !!franchiseId,
  });
}

/**
 * Create a customer, then - phone-first CRM - attach the opening phone number
 * if one was given. The `create` contract body has no phone field, so the phone
 * is a second `addPhone` write composed into the same user action. The customer
 * exists once `create` succeeds; a failing `addPhone` is surfaced via the
 * returned `phoneError` flag (non-fatal) rather than rolling the whole thing
 * back, so the caller can toast a soft warning and still land on the record.
 */
export function useCreateCustomer() {
  const franchiseId = useRestaurantStore((s) => s.selectedFranchiseId) ?? '';
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ phone, ...body }: CreateCustomerBody & { phone?: string }) => {
      const created = unwrapResult<Customer>(
        await openapiClient.POST(CUSTOMERS_PATH, { params: { path: { franchiseId } }, body }),
      );

      let phoneError = false;
      const trimmed = phone?.trim();
      if (trimmed) {
        try {
          unwrapResult(
            await openapiClient.POST('/api/customers/{customerId}/phones', {
              params: { path: { customerId: created.id } },
              body: { phone: trimmed },
            }),
          );
        } catch {
          phoneError = true;
        }
      }
      return { customer: created, phoneError };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: customerKeys.all(franchiseId) });
    },
  });
}

/**
 * Typeahead variant: small result set, only fires once the operator has typed
 * enough to be a real search. Powers "reuse an existing guest" pickers so the
 * customer pool grows by reuse, not duplicate records.
 */
export function useCustomerSearch(term: string) {
  const franchiseId = useRestaurantStore((s) => s.selectedFranchiseId) ?? '';
  const search = term.trim();

  return useQuery({
    queryKey: customerKeys.list(franchiseId, `typeahead:${search}`),
    queryFn: async () =>
      unwrapResult<CustomerList>(
        await openapiClient.GET(CUSTOMERS_PATH, {
          params: { path: { franchiseId }, query: { search, limit: 8 } },
        }),
      ),
    enabled: !!franchiseId && search.length >= 2,
  });
}
